import { motion } from "framer-motion";
import { useEffect } from "react";
import { useScene } from "./scene/SceneController";

const beforeItems = [
  "Searching through emails and folders",
  "Manual data entry and verification",
  "Following up for missing documents",
  "Spreadsheet-driven operations",
  "Reactive problem solving",
];

const afterItems = [
  "Reviewing intelligent insights",
  "Approving automated workflows",
  "Managing clients proactively",
  "Real-time firm visibility",
  "Focused advisory work",
];

const BeforeAfterSection = () => {
    const brandName = process.env.REACT_APP_BRAND_NAME;
  const { updateScene } = useScene();

  useEffect(() => {
    updateScene({
      section: "transformation",
      chaos: 0.1,
      organization: 1,
      coreIntensity: 3,
      cameraDistance: 8,
    });
  }, [updateScene]);

  return (
    <section
      id="transformation"
      className="tw-relative tw-min-h-screen tw-py-32 tw-px-6 tw-overflow-hidden"
    >
      <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-b tw-from-white tw-via-gray-100 tw-to-white" />

      <div className="tw-relative tw-z-10 tw-max-w-6xl tw-mx-auto">
        {/* Heading */}

        <motion.div
          initial={{
            opacity: 0,
            y: 40,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
          }}
          transition={{
            duration: 0.8,
          }}
          className="tw-text-center tw-max-w-4xl tw-mx-auto"
        >
          <p className="tw-text-blue-600 tw-uppercase tw-tracking-[0.3em] tw-text-sm tw-mb-5">
            The transformation
          </p>

          <h2 className="tw-text-4xl md:tw-text-6xl tw-font-bold tw-text-gray-900">
            Same accountants.
            <br />
            Completely different impact.
          </h2>

          <p className="tw-mt-6 tw-text-lg tw-text-gray-600">
            The goal is not fewer people. The goal is freeing your people to
            perform the work they were trained for.
          </p>
        </motion.div>

        {/* Comparison */}

        <div className="tw-mt-20 tw-grid md:tw-grid-cols-2 tw-gap-10">
          {/* BEFORE */}

          <motion.div
            initial={{
              opacity: 0,
              x: -60,
            }}
            whileInView={{
              opacity: 1,
              x: 0,
            }}
            viewport={{
              once: true,
            }}
            transition={{
              duration: 0.8,
            }}
            className="tw-rounded-3xl tw-p-10 tw-bg-gray-900 tw-text-white tw-shadow-2xl"
          >
            <div className="tw-inline-flex tw-px-4 tw-py-2 tw-rounded-full tw-bg-red-500/20 tw-text-red-300 tw-text-sm tw-mb-8">
              Traditional Workflow
            </div>

            <h3 className="tw-text-3xl tw-font-bold tw-mb-8">
              Accountants become data operators.
            </h3>

            <div className="tw-space-y-5">
              {beforeItems.map((item, index) => (
                <motion.div
                  key={item}
                  initial={{
                    opacity: 0,
                    x: -20,
                  }}
                  whileInView={{
                    opacity: 1,
                    x: 0,
                  }}
                  viewport={{
                    once: true,
                  }}
                  transition={{
                    delay: index * 0.1,
                  }}
                  className="tw-flex tw-gap-3 tw-items-center tw-text-gray-300"
                >
                  <span className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-red-400" />

                  {item}
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* AFTER */}

          <motion.div
            initial={{
              opacity: 0,
              x: 60,
            }}
            whileInView={{
              opacity: 1,
              x: 0,
            }}
            viewport={{
              once: true,
            }}
            transition={{
              duration: 0.8,
            }}
            className="tw-rounded-3xl tw-p-10 tw-bg-gradient-to-br tw-from-blue-600 tw-to-cyan-500 tw-text-white tw-shadow-2xl"
          >
            <div className="tw-inline-flex tw-px-4 tw-py-2 tw-rounded-full tw-bg-white/20 tw-text-white tw-text-sm tw-mb-8">
              {brandName} Workflow
            </div>

            <h3 className="tw-text-3xl tw-font-bold tw-mb-8">
              Accountants become trusted advisors.
            </h3>

            <div className="tw-space-y-5">
              {afterItems.map((item, index) => (
                <motion.div
                  key={item}
                  initial={{
                    opacity: 0,
                    x: 20,
                  }}
                  whileInView={{
                    opacity: 1,
                    x: 0,
                  }}
                  viewport={{
                    once: true,
                  }}
                  transition={{
                    delay: index * 0.1,
                  }}
                  className="tw-flex tw-gap-3 tw-items-center tw-text-white"
                >
                  <span className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-white" />

                  {item}
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Bottom message */}

        <motion.div
          initial={{
            opacity: 0,
            y: 30,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
          }}
          className="tw-mt-20 tw-text-center"
        >
          <h3 className="tw-text-3xl md:tw-text-4xl tw-font-bold tw-text-gray-900">
            Technology handles the repetition.
            <br />
            Humans deliver the expertise.
          </h3>
        </motion.div>
      </div>
    </section>
  );
};

export default BeforeAfterSection;
